import Gigya from './gigya.js'
import BaseParams from './interfaces/base-params.js'

export interface GetTopicSettingsResponse {
	CommunicationSettings: Record<
		string,
		{
			isActive: boolean
			description: string
			lastModified: string
			channel: string
			topic: string
			dependsOn: string[]
		}
	>
}

export type CommunicationSetting = GetTopicSettingsResponse['CommunicationSettings'][number]

export interface SetTopicSettings {
	CommunicationSettings: Record<string, Omit<CommunicationSetting, 'lastModified' | 'dependsOn'>>
}

export interface DeleteTopicSettings {
	CommunicationSettingsIds: string[]
}

export interface GetChannelsResponse {
	Channels: Record<
		string,
		{
			description: string
			dependsOn?: Array<{
				fieldID: string
				required: boolean
			}>
		}
	>
}

export interface SetChannelsParams {
	channels: Record<
		string,
		{
			// same literal value as Record key
			name: string
			description: string
			dependsOn: Array<{
				fieldID: string
				required: boolean
			}>
		}
	>
}

export interface DeleteChannelsParams {
	channelIDs: string[]
}

export class Communication {
	constructor(protected gigya: Gigya) {}

	/**
	 *
	 * This API retrieves all communication topics that were defined on the site.
	 * For more information, see Subscription to Communication Topics.
	 * @param params
	 * @see https://help.sap.com/docs/SAP_CUSTOMER_DATA_CLOUD/8b8d6fffe113457094a17701f63e3d6a/dc9a76429ef64862b6c9302480909472.html?locale=en-US
	 */
	public getTopicSettings(params: BaseParams = {}) {
		return this.gigya.request<GetTopicSettingsResponse>(
			'accounts.communication.getTopicSettings',
			params
		)
	}

	/**
	 * This API updates a communication topic that exists and is activated in Gigya, or creates it if it does not exist. For more information, see Subscription to Communication Topics.
	 * @param params
	 * @see https://help.sap.com/docs/SAP_CUSTOMER_DATA_CLOUD/8b8d6fffe113457094a17701f63e3d6a/7c53878d3e48427987ad86bc63a5f5fe.html?locale=en-US
	 */
	public setTopicSettings(params: BaseParams & SetTopicSettings) {
		return this.gigya.request('accounts.communication.setTopicSettings', params)
	}

	/**
	 * This API deletes a communication topic that exists in Gigya. For more information, see Subscription to Communication Topics.
	 * @param params
	 * @see https://help.sap.com/docs/SAP_CUSTOMER_DATA_CLOUD/8b8d6fffe113457094a17701f63e3d6a/5938074343c549e9a3cba3a8da3f6822.html?locale=en-US
	 */
	public deleteTopicSettings(params: BaseParams & DeleteTopicSettings) {
		return this.gigya.request('accounts.communication.deleteTopicSettings', params)
	}

	/**
	 * This API retrieves all communication channels that were defined on the site. For more information, see Subscription to Communication Topics.
	 * @param params
	 * @see https://help.sap.com/docs/SAP_CUSTOMER_DATA_CLOUD/8b8d6fffe113457094a17701f63e3d6a/e4a32b3dbe084a408dcdc5d36ba29597.html?locale=en-US
	 */
	public getChannels(params: BaseParams = {}) {
		return this.gigya.request<GetChannelsResponse>('accounts.communication.getChannels', params)
	}

	/**
	 * This API updates a communication channel that exists and is activated in Gigya, or creates it if it does not exist. For more information, see Subscription to Communication Topics.
	 * @param params
	 * @see https://help.sap.com/docs/SAP_CUSTOMER_DATA_CLOUD/8b8d6fffe113457094a17701f63e3d6a/9377da8a121c4c8588d0bbcca29b341f.html?locale=en-US
	 */
	public setChannels(params: BaseParams & SetChannelsParams) {
		return this.gigya.request('accounts.communication.setChannels', params)
	}

	/**
	 * This API deletes a communication channel that exists in the system. For more information, see Subscription to Communication Topics.
	 * @param params
	 * @see https://help.sap.com/docs/SAP_CUSTOMER_DATA_CLOUD/8b8d6fffe113457094a17701f63e3d6a/5d16230414634b28b7fe5c636beb41e2.html?locale=en-US
	 */
	public deleteChannels(params: BaseParams & DeleteChannelsParams) {
		return this.gigya.request('accounts.communication.deleteChannels', params)
	}
}

export default Communication
